/**
 * Navigation of the Lehrkraft-Bereich and the Bewerbungs-Bereich (order and texts as in the mockup): the items of the Vanilla
 * navigation drawer (…_SIDE_NAV, with „Hilfe & Weiterbildung“, see portalDrawerItems) and on phones additionally the bottom
 * navigation (TEACHER_NAV / APPLICANT_NAV).
 */
export type PortalNavItem = {
    /** Matches route meta `portalTab` */
    tab: string;
    text: string;
    to: string;
    icon: string;
    activeIcon: string;
    /**
     * Menu key (NavigationDrawerAccess, e.g. 'PAYOFF') the login needs for this page, through roles or personal guiAccess;
     * without it the tab is hidden and the route redirects to „Heute“ (see visiblePortalNav / portalTabAllowed)
     */
    guiAccess?: string;
};

export const TEACHER_NAV: PortalNavItem[] = [
    {tab: 'today', text: 'Heute', to: '/my-teaching/today', icon: 'mdi-home-outline', activeIcon: 'mdi-home'},
    {tab: 'schedule', text: 'Stundenplan', to: '/my-teaching/schedule', icon: 'mdi-calendar-blank-outline', activeIcon: 'mdi-calendar'},
    {tab: 'substitutions', text: 'Vertretungen', to: '/my-teaching/substitutions', icon: 'mdi-account-switch-outline', activeIcon: 'mdi-account-switch'},
    {tab: 'payoff', text: 'Abrechnung', to: '/my-teaching/payoff', icon: 'mdi-receipt-text-outline', activeIcon: 'mdi-receipt-text',
        guiAccess: 'PAYOFF'},
    {tab: 'profile', text: 'Profil', to: '/my-teaching/profile', icon: 'mdi-account-outline', activeIcon: 'mdi-account'},
];

export const APPLICANT_NAV: PortalNavItem[] = [
    {tab: 'application', text: 'Bewerbung', to: '/my-application', icon: 'mdi-file-account-outline', activeIcon: 'mdi-file-account'},
    {tab: 'profile', text: 'Profil', to: '/my-application/profile', icon: 'mdi-account-outline', activeIcon: 'mdi-account'},
    {tab: 'trainings', text: 'Schulungen', to: '/my-application/trainings', icon: 'mdi-school-outline', activeIcon: 'mdi-school'},
    {tab: 'contact', text: 'Kontakt', to: '/my-application/contact', icon: 'mdi-phone-outline', activeIcon: 'mdi-phone'},
];

/** „Verfügbarkeiten“: only in the menu (between Stundenplan and Vertretungen), on phones via Profil */
export const TEACHER_AVAILABILITIES_NAV: PortalNavItem = {
    tab: 'availabilities', text: 'Verfügbarkeiten', to: '/my-teaching/availabilities', icon: 'mdi-clock-outline', activeIcon: 'mdi-clock',
};

/** Menu (Vanilla navigation drawer) of the Lehrkraft-Bereich: bottom navigation + Verfügbarkeiten, Kündigungen, Hilfe & Weiterbildung */
export const TEACHER_SIDE_NAV: PortalNavItem[] = [
    ...TEACHER_NAV.slice(0, 2),
    TEACHER_AVAILABILITIES_NAV,
    ...TEACHER_NAV.slice(2),
    {tab: 'terminations', text: 'Kündigungen', to: '/my-teaching/terminations', icon: 'mdi-account-cancel-outline', activeIcon: 'mdi-account-cancel'},
    {tab: 'help', text: 'Hilfe & Weiterbildung', to: '/my-teaching/help', icon: 'mdi-lifebuoy', activeIcon: 'mdi-lifebuoy'},
];

export const APPLICANT_SIDE_NAV: PortalNavItem[] = APPLICANT_NAV;

/**
 * Items the login may see: items with `guiAccess` only with that menu key. `granted` are the effective menu keys
 * (NavigationDrawerAccess.getEffectiveAccess(store.state.guiAccess), the same rule as the office drawer and routeAccess.ts).
 */
export function visiblePortalNav(items: PortalNavItem[], granted: readonly string[]): PortalNavItem[] {
    return items.filter((item) => !item.guiAccess || granted.includes(item.guiAccess));
}

/** Route guard of the Lehrkraft-Bereich: a page whose tab (route meta portalTab) needs a menu key the login lacks is not opened */
export function portalTabAllowed(tab: string | undefined, granted: readonly string[]): boolean {
    const item = TEACHER_SIDE_NAV.find((navItem) => navItem.tab === tab);
    return !item?.guiAccess || granted.includes(item.guiAccess);
}

/** Active tab of the bottom navigation: pages without an own tab belong to „Heute“ (Hilfe) or „Profil“ (Verfügbarkeiten) */
export function bottomNavTab(tab: string): string {
    if (tab === 'availabilities') {
        return 'profile';
    }
    return tab === 'help' ? 'today' : tab;
}

export type PortalDrawerItem = { type: 'item'; text: string; icon: string; to: string };

/**
 * Items of the Vanilla navigation drawer in the portal: the portal pages (same drawer design as the office);
 * office logins with the teacher role additionally get „Zur Büro-Ansicht“.
 */
export function portalDrawerItems(portal: 'teacher' | 'applicant', items: PortalNavItem[], withOfficeLink: boolean): PortalDrawerItem[] {
    const out: PortalDrawerItem[] = items.map((item) => ({type: 'item', text: item.text, icon: item.icon, to: item.to}));
    if (withOfficeLink && portal === 'teacher') {
        out.push({type: 'item', text: 'Zur Büro-Ansicht', icon: 'mdi-office-building-outline', to: '/'});
    }
    return out;
}
